import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/app/auth";
import { useWorkspace } from "@/app/workspace";

function NavigationIcon({
  name,
}: {
  name: "home" | "settings" | "audit" | "collapse";
}) {
  const paths = {
    home: (
      <path d="M3 10.5 12 3l9 7.5v9a1.5 1.5 0 0 1-1.5 1.5h-5v-6h-5v6h-5A1.5 1.5 0 0 1 3 19.5v-9Z" />
    ),
    settings: (
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.05 2.05-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.04 1.56v.1h-2.9v-.1a1.7 1.7 0 0 0-1.04-1.56 1.7 1.7 0 0 0-1.88.34l-.06.06-2.05-2.05.06-.06A1.7 1.7 0 0 0 7.24 15a1.7 1.7 0 0 0-1.56-1.04h-.1v-2.9h.1A1.7 1.7 0 0 0 7.24 10a1.7 1.7 0 0 0-.34-1.88l-.06-.06L8.89 6l.06.06a1.7 1.7 0 0 0 1.88.34 1.7 1.7 0 0 0 1.04-1.56v-.1h2.9v.1A1.7 1.7 0 0 0 15.81 6a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.05 2.05-.06.06A1.7 1.7 0 0 0 19.4 10a1.7 1.7 0 0 0 1.56 1.04h.1v2.9h-.1A1.7 1.7 0 0 0 19.4 15Z" />
      </>
    ),
    audit: (
      <>
        <path d="M5 3.5h10l4 4V20a1.5 1.5 0 0 1-1.5 1.5h-12A1.5 1.5 0 0 1 4 20V5a1.5 1.5 0 0 1 1.5-1.5Z" />
        <path d="M15 3.5V8h4M8 12h8M8 16h6" />
      </>
    ),
    collapse: <path d="m14.5 6-6 6 6 6" />,
  };
  return (
    <svg
      className={`nav-icon nav-icon--${name}`}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const { hotel } = useWorkspace();
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebar-collapsed") === "true",
  );
  const sidebarRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeNav = () => setNavOpen(false);
  const toggleCollapsed = () =>
    setCollapsed((value) => {
      localStorage.setItem("sidebar-collapsed", String(!value));
      return !value;
    });
  useEffect(() => {
    if (!navOpen) return;
    sidebarRef.current?.querySelector<HTMLElement>("a, button")?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setNavOpen(false);
      menuButtonRef.current?.focus();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [navOpen]);
  const canManage = user?.role === "ADMIN" || user?.role === "SUPERVISOR";
  return (
    <div
      className={`app-shell ${collapsed ? "app-shell--sidebar-collapsed" : ""}`}
    >
      <a className="skip-link" href="#content">
        Saltar al contenido
      </a>
      <aside
        ref={sidebarRef}
        id="primary-navigation"
        className={`sidebar ${navOpen ? "sidebar--open" : ""} ${collapsed ? "sidebar--collapsed" : ""}`}
      >
        <Link
          className="brand-lockup"
          to="/"
          onClick={closeNav}
          title={collapsed ? "525 Hotel · Conciliaciones" : undefined}
        >
          <img src="/logo.png" alt="525 Hotel" />
          <div className="brand-lockup__copy">
            <span>Operaciones</span>
            <strong>Conciliaciones</strong>
          </div>
        </Link>
        <nav className="main-nav" aria-label="Navegación principal">
          <NavLink
            to="/"
            end
            onClick={closeNav}
            title={collapsed ? "Home" : undefined}
            className={({ isActive }) => (isActive ? "active" : "")}
          >
            <NavigationIcon name="home" />
            <span className="nav-label">Home</span>
          </NavLink>
          {canManage && (
            <>
              <NavLink
                to="/configuracion"
                onClick={closeNav}
                title={collapsed ? "Configuración" : undefined}
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                <NavigationIcon name="settings" />
                <span className="nav-label">Configuración</span>
              </NavLink>
              <NavLink
                to="/auditoria"
                onClick={closeNav}
                title={collapsed ? "Auditoría" : undefined}
                className={({ isActive }) => (isActive ? "active" : "")}
              >
                <NavigationIcon name="audit" />
                <span className="nav-label">Auditoría</span>
              </NavLink>
            </>
          )}
        </nav>
        <div className="sidebar-footer">
          <div
            className="user-chip"
            title={collapsed ? user?.displayName : undefined}
          >
            <span>{user?.displayName.slice(0, 1)}</span>
            <div>
              <strong>{user?.displayName}</strong>
              <small>{user?.role}</small>
            </div>
          </div>
          {!collapsed && (
            <button className="text-button" onClick={() => void logout()}>
              Cerrar sesión
            </button>
          )}
        </div>
        <button
          className="sidebar-collapse"
          onClick={toggleCollapsed}
          aria-label={collapsed ? "Expandir navegación" : "Colapsar navegación"}
          title={collapsed ? "Expandir navegación" : "Colapsar navegación"}
        >
          <NavigationIcon name="collapse" />
        </button>
      </aside>
      <div className="workspace">
        <button
          ref={menuButtonRef}
          className="mobile-menu"
          onClick={() => setNavOpen((open) => !open)}
          aria-controls="primary-navigation"
          aria-expanded={navOpen}
          aria-label={navOpen ? "Cerrar menú" : "Abrir menú"}
        >
          ☰
        </button>
        <main id="content" className="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
